import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { MessageCircle, ThumbsUp, ThumbsDown, Calendar, Users, TrendingUp, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

interface ConversationStats {
  totalConversations: number;
  uniqueSessions: number;
  helpfulCount: number;
  notHelpfulCount: number;
  escalatedCount: number;
  appointmentRequests: number;
  recentQuestions: { user_message: string; created_at: string }[];
  dailyStats: { date: string; count: number }[];
}

const ChatAnalytics = () => {
  const [stats, setStats] = useState<ConversationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStats = async () => {
    try {
      // Fetch all conversations
      const { data: conversations, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!conversations) {
        setStats(null);
        return;
      }

      // Calculate stats
      const uniqueSessions = new Set(conversations.map(c => c.session_id)).size;
      const helpfulCount = conversations.filter(c => c.was_helpful === true).length;
      const notHelpfulCount = conversations.filter(c => c.was_helpful === false).length;
      const escalatedCount = conversations.filter(c => c.escalated_to_contact).length;
      const appointmentRequests = conversations.filter(c => c.appointment_requested).length;

      // Get recent questions (exclude feedback entries)
      const recentQuestions = conversations
        .filter(c => c.user_message !== 'FEEDBACK' && c.user_message !== 'LEAD_CAPTURED' && c.user_message !== 'APPOINTMENT_REQUEST')
        .slice(0, 10)
        .map(c => ({ user_message: c.user_message, created_at: c.created_at }));

      // Calculate daily stats for last 7 days
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const dailyStats = last7Days.map(date => ({
        date,
        count: conversations.filter(c => 
          c.created_at?.startsWith(date) && 
          c.user_message !== 'FEEDBACK' && 
          c.user_message !== 'LEAD_CAPTURED'
        ).length
      }));

      setStats({
        totalConversations: conversations.filter(c => c.user_message !== 'FEEDBACK' && c.user_message !== 'LEAD_CAPTURED').length,
        uniqueSessions,
        helpfulCount,
        notHelpfulCount,
        escalatedCount,
        appointmentRequests,
        recentQuestions,
        dailyStats
      });
    } catch (error) {
      console.error('Error fetching chat stats:', error);
      toast({
        title: "Fehler",
        description: "Statistiken konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const clearAllData = async () => {
    if (!confirm('Möchten Sie wirklich alle Chat-Daten löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('chat_conversations')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (error) throw error;

      toast({
        title: "Gelöscht",
        description: "Alle Chat-Daten wurden gelöscht.",
      });

      fetchStats();
    } catch (error) {
      console.error('Error clearing data:', error);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Noch keine Chat-Daten vorhanden.</p>
      </div>
    );
  }

  const satisfactionRate = stats.helpfulCount + stats.notHelpfulCount > 0
    ? Math.round((stats.helpfulCount / (stats.helpfulCount + stats.notHelpfulCount)) * 100)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Chat-Statistiken</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={clearAllData}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Alle Daten löschen
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-cyan-100 rounded-lg">
                <MessageCircle className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Gespräche</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalConversations}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Einzigartige Besucher</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniqueSessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <ThumbsUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Zufriedenheit</p>
                <p className="text-2xl font-bold text-gray-900">
                  {satisfactionRate !== null ? `${satisfactionRate}%` : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Terminanfragen</p>
                <p className="text-2xl font-bold text-gray-900">{stats.appointmentRequests}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Eskaliert zu Kontakt</span>
              <span className="text-xl font-semibold text-gray-900">{stats.escalatedCount}</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Positives Feedback</span>
              <span className="text-xl font-semibold text-green-600">{stats.helpfulCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Negatives Feedback</span>
              <span className="text-xl font-semibold text-red-600">{stats.notHelpfulCount}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Letzte 7 Tage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end space-x-1 h-20">
              {stats.dailyStats.map((day, index) => {
                const maxCount = Math.max(...stats.dailyStats.map(d => d.count), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-cyan-500 rounded-t"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${day.date}: ${day.count} Gespräche`}
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      {new Date(day.date).toLocaleDateString('de-DE', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Letzte Fragen</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recentQuestions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Noch keine Fragen gestellt.</p>
          ) : (
            <div className="space-y-3">
              {stats.recentQuestions.map((q, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <MessageCircle className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 truncate">{q.user_message}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(q.created_at).toLocaleString('de-DE')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatAnalytics;
